/** @format */

import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CommentIcon from "@mui/icons-material/Comment";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import moment from "moment";
import { invokeApi, HTTP_METHODS } from "../../utils/http-service";
import { CF_APPROVAL_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";

// Generic comment thread on a CF record's exception-approval, alongside (not replacing) the
// Approve/Reject actions — a button with a badge count that opens the thread in a modal.
// Commenting on an already-APPROVED thread is what triggers the dedicated notification email
// on the backend.
const ExceptionApprovalComments = ({ id }) => {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [open, setOpen] = useState(false);

  const loadComments = () => {
    setLoading(true);
    invokeApi(HTTP_METHODS.GET, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${id}/comments`, null)
      .then((res) => setComments(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || "Failed to load comments"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (id) loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAddComment = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setPosting(true);
    invokeApi(HTTP_METHODS.POST, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${id}/comments`, { text: trimmed })
      .then(() => {
        setText("");
        loadComments();
      })
      .catch((err) => toast.error(err?.message || "Failed to add comment"))
      .finally(() => setPosting(false));
  };

  return (
    <>
      <Badge badgeContent={comments.length} color="primary">
        <Button variant="outlined" size="small" startIcon={<CommentIcon fontSize="small" />} onClick={() => setOpen(true)}>
          Comments for Legal Team
        </Button>
      </Badge>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Comments
          <IconButton size="small" onClick={() => setOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <CircularProgress size={18} />
          ) : (
            <Stack spacing={1} sx={{ mb: 1.5, maxHeight: 320, overflowY: "auto" }}>
              {comments.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No comments yet
                </Typography>
              )}
              {comments.map((c) => (
                <Box key={c.id || c._id} sx={{ p: 1, borderRadius: 1, bgcolor: "action.hover" }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {c.authorRole || c.authorId}
                    {c.createdAt ? ` · ${moment(c.createdAt).format("DD MMM YYYY, hh:mm A")}` : ""}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {c.text}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
          <Stack spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              size="small"
              label="Add comments"
              multiline
              minRows={3}
              placeholder="Add a comment"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <Button variant="outlined" size="small" disabled={posting || !text.trim()} onClick={handleAddComment}>
              Add
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExceptionApprovalComments;
